// SeoHead.js — Mantiene el título y la descripción de la pestaña al día mientras
// se navega dentro de la aplicación.
//
// El HTML que llega del servidor ya trae las etiquetas correctas
// (`backend/api/seo.php`), pero al ser una SPA la navegación no recarga la
// página: sin esto, el título se quedaría en el de la primera dirección que se
// abrió. Lee la misma configuración (`config_json.seo`), así que lo que se ve en
// la pestaña y lo que leen los buscadores dicen lo mismo.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useProject } from '../contexts/ProjectContext';

// Clave de la página dentro de `seo.paginas`: la primera parte de la ruta, con
// '/' para la portada. `/p/algo` y `/algo` son la misma página.
export const claveSeoDeRuta = (pathname = '/') => {
  const partes = String(pathname).split('/').filter(Boolean);
  if (partes.length >= 2 && partes[0] === 'p') partes.shift();
  return partes.length ? partes[0].toLowerCase() : '/';
};

// Texto plano de un campo que puede ser string o {es, pt, …}.
const texto = (v, idioma = 'es') => {
  if (v == null) return '';
  if (typeof v === 'object') return String(v[idioma] || v.es || Object.values(v)[0] || '');
  return String(v);
};

// Título y descripción efectivos: lo de la página, y lo que deje en blanco lo
// hereda del sitio.
export const resolverSeo = (seo = {}, pathname = '/', idioma = 'es') => {
  const pagina = (seo.paginas || {})[claveSeoDeRuta(pathname)] || {};
  return {
    titulo: texto(pagina.titulo, idioma) || texto(seo.titulo, idioma),
    descripcion: texto(pagina.descripcion, idioma) || texto(seo.descripcion, idioma),
  };
};

const ponerMeta = (nombre, contenido) => {
  if (!contenido) return;
  let el = document.querySelector(`meta[name="${nombre}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', nombre);
    document.head.appendChild(el);
  }
  el.setAttribute('content', contenido);
};

const SeoHead = () => {
  const { proyecto } = useProject();
  const location = useLocation();

  useEffect(() => {
    const seo = proyecto?.config_json?.seo;
    if (!seo) return;   // sin SEO configurado no se toca nada
    const { titulo, descripcion } = resolverSeo(seo, location.pathname);
    if (titulo) document.title = titulo;
    ponerMeta('description', descripcion);
  }, [proyecto, location.pathname]);

  return null;
};

export default SeoHead;
